import {
  Button, Card, Input, Select, Space, Table, Tag, Typography,
} from 'antd';
import rideBookingApi from 'api/rideBookingApi';
import ToastHelper from 'general/helpers/ToastHelper';
import useRouter from 'hooks/useRouter';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  formatBookingDate, formatBookingMoney, getRideBookingErrorMessage, RideBookingStatusTag, STATUS_OPTIONS,
} from '../../rideBookingsConstants';

const { Text, Title } = Typography;

const EMPTY_CRITERIA = { status: '', bookingCode: '' };

function RideBookingsListScreen() {
  const router = useRouter();
  const [filter, setFilter] = useState({ status: '', page: 1, pageSize: 20 });
  const [criteria, setCriteria] = useState(EMPTY_CRITERIA);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await rideBookingApi.getBookings(filter);
      setItems(response?.items || []);
      setTotal(response?.total || 0);
    } catch (error) {
      setItems([]);
      setTotal(0);
      ToastHelper.showError(getRideBookingErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const applyCriteria = () => setFilter((value) => ({ ...value, ...criteria, page: 1 }));
  const resetCriteria = () => {
    setCriteria(EMPTY_CRITERIA);
    setFilter((value) => ({ status: '', page: 1, pageSize: value.pageSize }));
  };

  const goToDetail = (bookingId) => router.navigate(`${bookingId}`);

  const columns = useMemo(() => [
    { title: 'Mã booking', dataIndex: 'bookingId', render: (value) => <Text strong>{value}</Text> },
    { title: 'Mã chuyến', dataIndex: 'tripCode' },
    {
      title: 'Khách',
      render: (_, row) => (
        <div>
          <Text strong>{row.customer?.fullname || `User #${row.customerId}`}</Text>
          <br />
          <Text type="secondary">{row.customer?.phone || row.customer?.email || ''}</Text>
        </div>
      ),
    },
    {
      title: 'Tài xế',
      render: (_, row) => (
        <div>
          <Text strong>{row.driver?.fullname || '—'}</Text>
          <br />
          <Text type="secondary">{row.driver?.phone || row.driver?.email || ''}</Text>
        </div>
      ),
    },
    {
      title: 'Đón/trả',
      render: (_, row) => (
        <div>
          {row.pickup?.address || '—'}
          <br />
          <Text type="secondary">
            →
            {' '}
            {row.dropoff?.address || '—'}
          </Text>
        </div>
      ),
    },
    { title: 'Ghế', render: (_, row) => (row.isCharter ? <Tag color="gold">Bao xe {row.charterVehicleCapacity || 4} chỗ</Tag> : row.seats) },
    { title: 'Tổng tiền', dataIndex: 'totalFare', render: formatBookingMoney },
    { title: 'Trạng thái', dataIndex: 'status', render: (value) => <RideBookingStatusTag status={value} /> },
    { title: 'Tạo lúc', dataIndex: 'createdAt', render: formatBookingDate },
    { title: '', render: (_, row) => <Button type="link" onClick={() => goToDetail(row.bookingId)}>Chi tiết</Button> },
  ], []);

  return (
    <div className="container-fluid px-4 my-4">
      <Card>
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
          <div>
            <Title level={4} className="mb-0">Booking xe ghép</Title>
            <Text type="secondary">
              Tổng
              {' '}
              {total}
              {' '}
              booking
            </Text>
          </div>
          <Button onClick={load} loading={loading}>Làm mới</Button>
        </div>
        <Space wrap size={[12, 12]} style={{ marginBottom: 20 }}>
          <Input
            value={criteria.bookingCode}
            placeholder="Mã booking"
            allowClear
            style={{ width: 220 }}
            onChange={(event) => setCriteria((value) => ({ ...value, bookingCode: event.target.value }))}
            onPressEnter={applyCriteria}
          />
          <Select
            value={criteria.status}
            options={STATUS_OPTIONS}
            style={{ width: 200 }}
            onChange={(status) => setCriteria((value) => ({ ...value, status }))}
          />
          <Button type="primary" style={{ backgroundColor: '#30BD6F', borderColor: '#30BD6F' }} onClick={applyCriteria}>Lọc</Button>
          <Button onClick={resetCriteria}>Xóa lọc</Button>
        </Space>
        <Table
          rowKey="bookingId"
          dataSource={items}
          columns={columns}
          loading={loading}
          scroll={{ x: 1200 }}
          locale={{ emptyText: 'Không có dữ liệu' }}
          pagination={{
            current: filter.page,
            pageSize: filter.pageSize,
            total,
            showSizeChanger: true,
            onChange: (page, pageSize) => setFilter((value) => ({ ...value, page, pageSize })),
          }}
          onRow={(row) => ({ onDoubleClick: () => goToDetail(row.bookingId) })}
        />
      </Card>
    </div>
  );
}

export default RideBookingsListScreen;
